<script setup lang="ts">
import HugeIcon from '../HugeIcon/HugeIcon.vue';
import { useControllableModel } from '../composables/useControllableModel';
import type { DisclosureItemProps } from './disclosure-item';

const props = withDefaults(defineProps<DisclosureItemProps>(), {
  defaultOpen: false,
});

const openModel = defineModel<boolean>('open');

const { isOpen, toggle } = useControllableModel(
  openModel,
  () => props.defaultOpen,
);
</script>

<template>
  <div
    class="w-full overflow-hidden rounded-lg border transition-colors"
    :class="
      isOpen
        ? 'border-primary-200 bg-primary-50/40 shadow-sm'
        : 'border-neutral-200 bg-white'
    "
  >
    <button
      type="button"
      class="flex w-full items-center justify-between p-4 text-left transition-colors"
      :class="
        isOpen ? 'bg-primary-50/60 hover:bg-primary-50' : 'hover:bg-neutral-50'
      "
      :aria-expanded="isOpen"
      @click="toggle"
    >
      <span class="flex min-w-0 flex-1 items-center gap-3">
        <slot name="leading" />
        <span class="flex min-w-0 flex-1 flex-col gap-0.5">
          <slot name="title">
            <span class="flex items-center gap-2">
              <span class="text-sm font-semibold text-neutral-700">
                {{ props.title }}
              </span>
              <span
                v-if="props.tag"
                class="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-500"
              >
                {{ props.tag }}
              </span>
            </span>
          </slot>
        </span>
      </span>
      <span
        v-if="$slots.trailing"
        class="mr-3 flex shrink-0 items-center gap-2 self-center"
      >
        <slot name="trailing" />
      </span>
      <HugeIcon
        icon="chevron_down"
        :size="16"
        class="shrink-0 self-center text-neutral-400 transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>

    <div
      class="grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-in-out"
      :class="{ 'grid-rows-[1fr]': isOpen }"
    >
      <div
        class="overflow-hidden border-t"
        :class="isOpen ? 'border-primary-100 bg-white' : 'border-neutral-100'"
      >
        <slot />
      </div>
    </div>
  </div>
</template>
